import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Title } from '@mantine/core'
import { useSetState } from '@mantine/hooks'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import Yup from '@/utils/yup'
import { withServerSideProps } from '@/containers/withServerSideProps'
import { useAuth } from '@/containers/auth'
import { APP_LOCALE } from '@/constants/define'
import { ProductKeyApi } from '@/api/product-key/index.api'
import RedeemInput from './components/redeem-input'
import RedeemStatusPopup, {
  TRedeemStatusPopup,
} from './components/redeem-status-popup'

const productKeyApi = new ProductKeyApi()

const KeyClaimPage = () => {
  const formRef = useRef<HTMLFormElement>(null)
  const {
    setOpenAuthModal,
    setAuthModalState,
    isAuthenticated,
    setOnAuthModalClose,
    refreshUser,
  } = useAuth()
  const [statusPopupOpened, setPopupOpened] = useState(false)
  const [popupState, setPopupState] = useSetState<
    Pick<TRedeemStatusPopup, 'status' | 'errorMessage'>
  >({
    status: 'success',
    errorMessage: '',
  })
  const schema = Yup.object().shape({
    user_input: Yup.string().required().length(22),
  })

  const { t } = useTranslation()
  const {
    reset,
    watch,
    formState: { isValid, isSubmitting },
    control,
    handleSubmit,
  } = useForm({
    mode: 'all',
    defaultValues: {
      user_input: '',
    },
    resolver: yupResolver(schema),
  })

  const watchInput = watch('user_input')

  const submitForm = useCallback(
    async data => {
      if (!isValid || isSubmitting) {
        return
      }

      const isAuth = await refreshUser()
      if (!isAuth) {
        toast.info(t('redeem-require-login'), {
          position: 'top-center',
        })
        setOpenAuthModal(true)
        return
      }
      // 根据接口返回情况设置popup状态
      const res = await productKeyApi.redeem(data.user_input)
      if (res.success) {
        setPopupState({
          status: 'success',
        })
      } else {
        setPopupState({
          status: 'fail',
          errorMessage: res.message,
        })
      }
      setPopupOpened(true)
    },
    [isSubmitting, isValid, refreshUser, setOpenAuthModal, setPopupState, t]
  )

  useEffect(() => {
    setAuthModalState({
      initialStage: 'smsLogin',
    })
  }, [setAuthModalState])

  useEffect(() => {
    setOnAuthModalClose(async isLoggedIn => {
      if (isLoggedIn) {
        await handleSubmit(submitForm)()
      }
    })
    return () => {
      setOnAuthModalClose(undefined)
    }
  }, [handleSubmit, setOnAuthModalClose, submitForm])

  return (
    <div
      style={{
        backgroundSize: '2000px',
      }}
      className="bg-white bg-repeat min-h-screen flex flex-col bg-[url('https://res.ggacademy.gg/user-web/images/backgrounds/bg-faq.png')]"
    >
      <RedeemStatusPopup
        open={statusPopupOpened}
        onClose={() => {
          // 重置表单
          setPopupOpened(false)
          reset()
        }}
        {...popupState}
      />
      <main className="container max-w-sm mx-auto flex flex-col items-center flex-1 h-1 w-full mt-32">
        <div className="mb-10 text-center">
          <Title order={3}>{t('redeem-a-code')}</Title>
        </div>

        <form
          className="w-full px-1"
          ref={formRef}
          onSubmit={handleSubmit(submitForm)}
        >
          <div className="mb-8">
            <Controller
              control={control}
              name={'user_input'}
              render={({ field: { onChange, value } }) => {
                return <RedeemInput onChange={onChange} value={value} />
              }}
            />
          </div>
          <div className="my-4">
            <Button
              loading={isSubmitting}
              size="md"
              disabled={!watchInput || !isValid || isSubmitting}
              fullWidth
              type="submit"
            >
              {t('submit')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  )
}

export default KeyClaimPage

const getServerSideProps = withServerSideProps(
  undefined,
  APP_LOCALE === 'zh_CN' ? ['footerLink'] : undefined
)

export { getServerSideProps }
